<template>
  <div class="wagers-view">
    <div class="view-header">
      <h1>{{ $t('nav.wagers') }}</h1>
    </div>
    <ToastComponent v-if="toast" :title="toast.title" :message="toast.message" :type="toast.type" :duration="toast.duration" />
    <div v-if="!isAuthenticated" class="auth-error">
      <p>{{ $t('auth.authenticationRequired') }} <a href="#" @click.prevent="handleLoginRequest" class="login-link">{{ $t('auth.pleaseLoginLink') }}</a>.</p>
    </div>

    <TransactionsTable 
      v-else
      :transactions="transactionsStore.transactions"
      :loading="transactionsStore.loading"
      @view-details="viewDetails"
      @rebet="handleRebet"
    />

    <BaseModal
      :is-open="isDetailsModalOpen"
      :title="$t('wagers.details')"
      @close="closeDetailsModal"
    >
      <TransactionDetails
        v-if="transactionsStore.currentTransaction"
        :transaction="transactionsStore.currentTransaction"
        @rebet="handleRebet"
      />
    </BaseModal>

    <div v-if="transactionsStore.error" class="error-message">
      {{ transactionsStore.error }}
    </div>

    <div v-if="isAuthenticated" class="pagination-controls">
      <BaseButton 
        variant="outline"
        class="pagination-button"
        :disabled="transactionsStore.loading || transactionsStore.pagination.current_page === 1" 
        @click="goToPage(transactionsStore.pagination.current_page - 1)">
        {{ $t('common.previous') }}
      </BaseButton>
      <span>{{ $t('common.page') }} {{ transactionsStore.pagination.current_page }} {{ $t('common.of') }} {{ transactionsStore.pagination.total_pages }}</span>
      <BaseButton 
        variant="outline"
        class="pagination-button"
        :disabled="transactionsStore.loading || transactionsStore.pagination.current_page === transactionsStore.pagination.total_pages" 
        @click="goToPage(transactionsStore.pagination.current_page + 1)">
        {{ $t('common.next') }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useTransactionsStore } from '@/stores/transactions'
import { useAuthStore } from '@/stores/auth'
import { useLoaderStore } from '@/stores/loader'
import { useToast } from '@/composables/useToast'
import ToastComponent from '@/components/ToastComponent.vue'
import TransactionsTable from '@/components/wagers/TransactionsTable.vue'
import type { Transaction } from '@/types/transaction'
import BaseButton from '@/components/BaseButton.vue'
import { useBetslipStore } from '@/stores/betslip'
import { useGameStore } from '@/stores/game'
import { useModalStore } from '@/stores/modal'
import { useRoute } from 'vue-router'
import { iframeCommunication } from '@/utils/iframeCommunication'
import BaseModal from '@/components/BaseModal.vue'
import TransactionDetails from '@/components/wagers/TransactionDetails.vue'
import { useI18n } from 'vue-i18n'

const transactionsStore = useTransactionsStore()
const authStore = useAuthStore()
const loaderStore = useLoaderStore()
const { toast, showToast } = useToast()
const { t } = useI18n()

const betslipStore = useBetslipStore()
const { addBet } = betslipStore
const gameStore = useGameStore()
const route = useRoute()
const isDetailsModalOpen = ref(false)
const savedScrollPosition = ref(0)

// Get the expand reference from query params (for auto-opening modal)
const expandReference = ref<string | null>(route.query.expand ? (route.query.expand as string) : null)

const isAuthenticated = computed(() => authStore.isAuthenticated)

const handleLoginRequest = () => {
  iframeCommunication.sendLoginRequest('wagers-view-required')
  return;
}

// Watch for transactions to be loaded and auto-expand if needed
watch(
  () => transactionsStore.transactions,
  (transactions) => {
    if (transactions && transactions.length > 0 && expandReference.value && !isDetailsModalOpen.value) {
      const transaction = transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        viewDetails(transaction)
      }
    }
  },
  { immediate: true }
)

const viewDetails = async (wager: Transaction) => {
  try {
    savedScrollPosition.value = window.pageYOffset || document.documentElement.scrollTop
    loaderStore.show(t('wagers.loadingDetails'))
    await transactionsStore.fetchTransactionDetail(wager.reference)
    isDetailsModalOpen.value = true
  } catch (err) {
    isDetailsModalOpen.value = false
  } finally {
    loaderStore.hide()
  }
}

const closeDetailsModal = () => {
  isDetailsModalOpen.value = false
  nextTick(() => {
    window.scrollTo({
      top: savedScrollPosition.value,
      behavior: 'auto'
    })
  })
}

const goToPage = (page: number) => {
  transactionsStore.setPage(page)
}

const handleRebet = async () => {
  try {
    const transaction = transactionsStore.currentTransaction
    if (!transaction || !transaction.games) {
      showToast({
        title: t('common.error'),
        message: t('betslip.noTransactionData'),
        type: 'error'
      })
      return
    }

    let rebetCount = 0
    
    for (const [gameKey, games] of Object.entries(transaction.games)) {
      for (const game of (games as any[])) {
        await gameStore.fetchGameDetails(gameKey)
        const nextDraws = gameStore.getNextDraws
        const marketGroups = gameStore.getMarketGroups
        
        for (const bet of game.bets) {
          // Find matching market and outcome based on bet information
          let matchedMarket = null
          let matchedOutcome = null
          let matchedMarketGroup = null
          
          // Try to find the market and outcome that matches this bet
          for (const marketGroup of marketGroups) {
            for (const market of marketGroup.markets) {
              // Match by market name and bet type
              if (market.name === bet.market_name && market.outcomes) {
                // Try to find matching outcome by odds and text
                for (const outcome of market.outcomes) {
                  if (Math.abs(parseFloat(outcome.odds) - parseFloat(bet.odds)) < 0.01) {
                    matchedMarket = market
                    matchedOutcome = outcome
                    matchedMarketGroup = marketGroup
                    break
                  }
                }
                if (matchedOutcome) break
              }
            }
            if (matchedOutcome) break
          }
          
          // If we couldn't find a match, skip this bet
          if (!matchedMarket || !matchedOutcome || !matchedMarketGroup) {
            console.warn('Could not find matching market/outcome for bet:', bet)
            continue
          }
          
          const betslipItem = {
            market_id: matchedMarket.id,
            market_group_id: matchedMarketGroup.id,
            game_name: gameStore.getGameDetail?.name,
            game_id: gameStore.getGameDetail?.id || 0,
            market_name: bet.market_name || '',
            market_description: bet.market_group_description || '',
            numbers: bet.bet_numbers || [],
            stake: Number(bet.bet_cost),
            betType: bet.betType,
            maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
            odds: Number(bet.odds),
            draws: nextDraws && nextDraws.length > 0 ? [nextDraws[0]] : [],
            allAvailableDraws: nextDraws || [],
            drawCount: 1,
            picks: (bet as any).picks || 0,
            numBets: 1,
            outcome_id: matchedOutcome.id.toString(),
            label: bet.market_name || '',
            specifier: matchedOutcome.specifier || null,
            select_numbers: matchedOutcome.select_numbers || false,
            currency_symbol: gameStore.getBetSettings?.currency_symbol || (transaction as any).currency_symbol || '€'
          }
          
          const added = addBet(betslipItem)
          if (added) {
            rebetCount++
          }
        }
      }
    }

    const modalStore = useModalStore()
    isDetailsModalOpen.value = false
    modalStore.openBetslip()
  } catch (error) {
    console.error('Rebet error:', error)
    showToast({
      title: t('common.error'),
      message: t('betslip.failedToAddBets'),
      type: 'error'
    })
  }
}

onMounted(async () => {
  try {
    // Check if authentication parameters are present in URL or session
    const route = useRoute()
    const hasToken = !!route.query.token
    const hasAccount = !!route.query.account
    const hasSessionAuth = authStore.hasValidSession()
    
    if (!hasToken && !hasAccount && !hasSessionAuth) {
      return // Silently return without showing message
    }
    
    const authSuccess = await authStore.authenticate()
    
    if (!authSuccess) {
      return
    }
    
    await transactionsStore.fetchTransactions()
    
    // Check if there's an expand query parameter to auto-expand a transaction
    if (expandReference.value && !isDetailsModalOpen.value) {
      // Find the transaction with this reference
      const transaction = transactionsStore.transactions.find(t => t.reference === expandReference.value)
      if (transaction) {
        // Auto-expand this transaction
        await viewDetails(transaction)
      }
    }
  } catch (err: any) {
    console.log(err)
    
    // Check if it's a 401 error (authentication failed)
    if (err?.response?.status === 401) {
      authStore.clearInvalidTokenKeepAccount()
      // Don't return here - let the component render the auth error message
    } else {
      showToast({
        title: t('common.error'),
        message: t('toast.failedToLoadWagers'),
        type: 'error'
      })
    }
  } finally {
    loaderStore.hide()
  }
})
</script>

<style scoped>
.wagers-view {
  min-height: 100%; /* Changed from 100vh to prevent iframe infinite loop */
  background: var(--bg-primary);
  color: var(--text-primary);
  padding-bottom: 2rem;
}

/* Fix for infinite resize loop in iframe */
:global(body.iframe-mode) .wagers-view {
  min-height: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding-bottom: 20px; 
}

/* Fix for infinite resize loop in iframe */
:global(body.iframe-mode) .wagers-view {
  min-height: auto !important;
  height: auto !important;
}

.view-header {
  background: var(--bg-view-header, var(--bg-dark));
  padding: 16px 24px;
  margin-bottom: 1px;
}

.view-header h1 {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

/* Shared button styles */
.pagination-button,
.pagination-controls button {
  padding: 0.5rem 1.5rem;
  background: var(--accent-color);
  color: var(--text-button, var(--text-white));
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.pagination-button:hover:not(:disabled),
.pagination-controls button:hover:not(:disabled) {
  background: var(--bg-button-hover);
}

.pagination-controls button:disabled {
  background: var(--bg-button-disabled);
  cursor: not-allowed;
}

/* Shared error message styles */
.auth-error,
.error-message {
  background: var(--error-bg);
  color: var(--error-color);
  margin: 16px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid var(--error-border);
}

.auth-error {
  padding: 24px;
}

.auth-error p {
  margin: 8px 0;
  font-size: 14px;
}

.auth-error p:first-child {
  font-weight: 500;
  font-size: 16px;
}

.login-link {
  color: var(--accent-color);
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s;
}

.login-link:hover {
  opacity: 0.8;
}

.error-message {
  padding: 16px;
  border-radius: 4px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
}

@media (max-width: 768px) {
  .view-header {
    padding: 16px;
  }

  .view-header h1 {
    font-size: 20px;
  }
  
  .auth-error,
  .error-message {
    margin: 8px;
    padding: 16px;
  }
  
  .pagination-controls {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 0.5rem;
    margin: 1rem 16px;
    padding: 0;
    align-items: center;
  }
  
  .pagination-button {
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 6px;
    text-align: center;
    justify-self: center;
    min-width: 0;
    width: 100%;
    max-width: 120px;
  }
  
  .pagination-controls span {
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
    grid-column: 2;
  }
}
</style>
